package com.schoolconnect.app.feature.examresults

import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.repository.examresult.ExamResultRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.math.roundToInt

class ParentExamResultsViewModel(initialChildren: List<ParentAttendanceChild> = emptyList()) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private val _state = MutableStateFlow(ParentExamResultsState(children = initialChildren))
    val state: StateFlow<ParentExamResultsState> = _state.asStateFlow()

    init {
        observeGlobalSelection()
        loadSelectedChild()
    }

    private fun observeGlobalSelection() {
        scope.launch {
            ParentChildSelectionStore.selectedChildId.collect { childId ->
                val index = _state.value.children.indexOfFirst { it.id == childId }
                if (index >= 0 && index != _state.value.selectedChildIndex) onEvent(ParentExamResultsEvent.ChildSelected(index))
            }
        }
    }

    fun onEvent(event: ParentExamResultsEvent) {
        when (event) {
            is ParentExamResultsEvent.ChildSelected -> {
                val index = event.index.coerceIn(0, _state.value.children.lastIndex.coerceAtLeast(0))
                _state.update { it.copy(selectedChildIndex = index, pageIndex = 0, totalCount = 0, isLoading = true, errorMessage = "") }
                loadSelectedChild()
            }
            is ParentExamResultsEvent.SearchChanged -> { _state.update { it.copy(search = event.value, pageIndex = 0, isLoading = true) }; loadSelectedChild(debounce = true, includeSummary = false) }
            is ParentExamResultsEvent.PageChanged -> { _state.update { it.copy(pageIndex = event.index.coerceAtLeast(0), isLoading = true) }; loadSelectedChild(includeSummary = false) }
            is ParentExamResultsEvent.PageSizeChanged -> { _state.update { it.copy(pageSize = event.size.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0, isLoading = true) }; loadSelectedChild(includeSummary = false) }
            is ParentExamResultsEvent.SortChanged -> {
                _state.update { current -> current.copy(sortField = event.field, sortAscending = if (current.sortField == event.field) !current.sortAscending else true, pageIndex = 0, isLoading = true) }
                loadSelectedChild(includeSummary = false)
            }
            is ParentExamResultsEvent.ViewRequested -> loadDetail(event.row)
            ParentExamResultsEvent.ViewDismissed -> _state.update { it.copy(viewing = null, viewingDetail = null, isViewingLoading = false, viewingError = "") }
            ParentExamResultsEvent.Retry -> loadSelectedChild()
        }
    }

    private fun loadSelectedChild(debounce: Boolean = false, includeSummary: Boolean = true) {
        loadJob?.cancel()
        val query = _state.value
        val studentId = query.selectedChild?.id.orEmpty().trim()
        if (studentId.isBlank()) {
            _state.update { it.copy(rows = emptyList(), totalCount = 0, summary = ParentExamResultsSummaryUi(), isLoading = false, errorMessage = "No linked child was found.") }
            return
        }
        loadJob = scope.launch {
            if (debounce) delay(300)
            val results = ExamResultRepository.loadParentResults(
                studentId = studentId,
                search = query.search,
                pageIndex = query.pageIndex,
                pageSize = query.pageSize,
                sortBy = query.sortField.apiValue,
                order = if (query.sortAscending) "asc" else "desc"
            )
            val summary = if (includeSummary) ExamResultRepository.loadParentSummary(studentId) else null
            results.fold(
                onSuccess = { raw ->
                    val rows = raw.toRows()
                    val totalCount = raw.firstInt("count")
                    if (totalCount == null) {
                        _state.update { it.copy(isLoading = false, errorMessage = "Exam results response did not include the required count.") }
                        return@fold
                    }
                    val window = com.schoolconnect.app.feature.common.pageWindow(query.pageIndex, query.pageSize, totalCount)
                    if (window.pageIndex != query.pageIndex) {
                        _state.update { it.copy(totalCount = totalCount, pageIndex = window.pageIndex) }
                        loadSelectedChild(includeSummary = false)
                        return@fold
                    }
                    val apiSummary = summary?.getOrNull()?.toSummary()
                    val displayedSummary = apiSummary?.let { value ->
                        if (value.totalExams == "0" && totalCount > 0) value.copy(totalExams = totalCount.toString()) else value
                    }
                    _state.update { it.copy(rows = rows, totalCount = totalCount, summary = displayedSummary ?: it.summary, isLoading = false, errorMessage = "") }
                },
                onFailure = { error -> _state.update { it.copy(isLoading = false, errorMessage = error.message ?: "Unable to load exam results.") } }
            )
        }
    }

    private fun loadDetail(row: ParentExamResultRowUi) {
        val studentId = _state.value.selectedChild?.id.orEmpty().trim()
        if (studentId.isBlank() || row.id.isBlank()) return
        _state.update { it.copy(viewing = row, viewingDetail = null, isViewingLoading = true, viewingError = "") }
        scope.launch {
            ExamResultRepository.loadParentDetail(row.id, studentId).fold(
                onSuccess = { raw -> _state.update { it.copy(viewingDetail = raw.toDetail(row, it.selectedChild), isViewingLoading = false) } },
                onFailure = { error -> _state.update { it.copy(isViewingLoading = false, viewingError = error.message ?: "Unable to load exam result details.") } }
            )
        }
    }

    fun clear() = scope.cancel()
}

private fun JsonObject.toRows(): List<ParentExamResultRowUi> = firstArray("exam_results", "results", "exams", "items").mapNotNull { it.jsonObject.toRow() }

private fun JsonObject.toRow(): ParentExamResultRowUi? {
    val id = firstString("exam_id", "id") ?: return null
    val total = firstNumberString("total_obtained", "total_marks_obtained", "marks_obtained", "total_marks") ?: "—"
    val max = firstNumberString("total_max", "max_marks", "total_max_marks", "out_of") ?: "—"
    val percentage = firstNumberString("percentage", "percentage_marks", "average_percentage") ?: percentage(total, max)
    return ParentExamResultRowUi(id, firstString("exam_name", "name", "title") ?: "—", (firstString("start_date", "exam_start_date", "date") ?: "").toDisplayDate(), "$total / $max", percentage?.let { "$it%" } ?: "—", firstString("grade", "overall_result", "result") ?: "—", firstString("remarks", "remark", "teacher_remarks") ?: "—", firstString("detail", "description") ?: "")
}

private fun JsonObject.toSummary(): ParentExamResultsSummaryUi = ParentExamResultsSummaryUi(
    totalExams = firstNumberString("total_exams", "exam_count", "total") ?: "—",
    overallAverage = (firstNumberString("overall_avg_percentage", "overall_average_percentage", "overall_average", "average_percentage", "percentage") ?: "—").let { if (it == "—") it else "$it%" },
    bestSubject = firstString("best_performance_subject", "best_performing_subject", "best_subject", "highest_subject") ?: "—",
    needsImprovementSubject = firstString("needs_improvement_subject", "improvement_subject", "lowest_subject") ?: "—"
)

private fun JsonObject.toDetail(row: ParentExamResultRowUi, child: ParentAttendanceChild?): ParentExamResultDetailUi = ParentExamResultDetailUi(
    studentName = firstString("student_name", "name") ?: child?.name.orEmpty(),
    className = firstString("class_name", "class", "class_section") ?: child?.classLabel.orEmpty(),
    rollNumber = firstString("roll_number", "roll_no") ?: child?.rollNumber.orEmpty(),
    exam = firstString("exam_name", "name", "title") ?: row.exam,
    startDate = (firstString("start_date", "exam_start_date", "date") ?: row.date).toDisplayDate(),
    totalMarks = firstNumberString("total_obtained", "total_marks_obtained", "marks_obtained", "total_marks") ?: row.marks.substringBefore('/').trim(),
    outOf = firstNumberString("total_max", "max_marks", "total_max_marks", "out_of") ?: row.marks.substringAfter('/', "—").trim(),
    percentage = (firstNumberString("percentage", "average_percentage", "overall_average_percentage") ?: row.percentage.removeSuffix("%")).let { if (it == "—") it else "$it%" },
    grade = firstString("grade") ?: row.overall.takeUnless { it.startsWith("Rank") }.orEmpty(),
    rank = firstString("rank", "class_rank") ?: row.overall.takeIf { it.startsWith("Rank") }?.removePrefix("Rank")?.trim().orEmpty().ifBlank { "—" },
    subjects = firstArray("subject_results", "subject_wise_results", "subjects", "breakdown").map { it.jsonObject.toSubject() },
    remarks = firstString("remarks", "remark", "teacher_remarks", "notes") ?: row.remarks
)

private fun JsonObject.toSubject(): ParentExamSubjectResultUi {
    val marks = firstNumberString("marks", "marks_obtained", "score") ?: "—"
    val total = firstNumberString("total_marks", "max_marks", "out_of") ?: "—"
    return ParentExamSubjectResultUi(firstString("subject_name", "subject", "name") ?: "—", firstString("grade") ?: "—", firstString("rank", "subject_rank") ?: "—", marks, total, (firstNumberString("percentage", "percentage_marks") ?: percentage(marks, total))?.let { "$it%" } ?: "—")
}

private fun JsonObject.firstString(vararg keys: String): String? = keys.firstNotNullOfOrNull { key -> get(key)?.jsonPrimitive?.contentOrNull?.takeIf(String::isNotBlank) }
private fun JsonObject.firstNumberString(vararg keys: String): String? = firstString(*keys)?.toDisplayNumber()
private fun JsonObject.firstArray(vararg keys: String): JsonArray = keys.firstNotNullOfOrNull { get(it)?.jsonArray } ?: JsonArray(emptyList())
private fun JsonObject.firstInt(vararg keys: String): Int? = firstString(*keys)?.toIntOrNull()
private fun String.toDisplayNumber(): String = toDoubleOrNull()?.let { if (it % 1.0 == 0.0) it.toInt().toString() else it.toString() } ?: this
private fun percentage(marks: String, total: String): String? = marks.toDoubleOrNull()?.let { score -> total.toDoubleOrNull()?.takeIf { it != 0.0 }?.let { max -> ((score / max) * 100.0).let { value -> if (value % 1.0 == 0.0) value.toInt().toString() else (value * 10.0).roundToInt().toDouble().div(10.0).toString() } } }
private fun String.toDisplayDate(): String = take(10).split("-").let { parts -> if (parts.size == 3) "${parts[2]} ${listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[parts[1].toIntOrNull()?.minus(1) ?: 0]} ${parts[0]}" else this }
